import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Carrier } from '../../../../user-dashboard/shared/carrier.model';
import { noop, Observable, Subscription } from 'rxjs';
import {
  SearchValidity,
  SearchValidityKey,
  TruckJourneyInfo,
  TruckJourneyInfoOpenable,
  TruckJourneySearchCriteria
} from '../../models/truck-journey.model';
import * as moment from 'moment/moment';
import { TruckJourneyService } from '../../services/truck-journey.service';
import { DataFiltersComponent, HELPER_POSITION } from '../../../../shared/data-filters/data-filters.component';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { NavigationService } from '../../../../shared/services/navigation.service';
import { TruckJourneyRequestMapper } from '../../mappers/truck-journey-request.mapper';
import { TranslatedToastrService } from '../../../../shared/services/translated-toastr.service';
import { finalize, map } from 'rxjs/operators';
import { LoaderService } from '../../../../shared/services/loader.service';
import { cloneDeep } from 'lodash';

enum GroupStatus {
  CLOSED = 'CLOSED',
  OPENED = 'OPENED',
  EDITED = 'EDITED'
}

interface TruckJourneyInfoEditableGroup {
  truckJourneys: TruckJourneyInfoOpenable[];
  status: GroupStatus;
}

type TruckJourneyDayGroup = Map<string, TruckJourneyInfoEditableGroup>;
const LOAD_FETCH_MAX_TIME_WINDOW_IN_DAYS = 7;

@Component({
  selector: 'app-truck-journey-list',
  templateUrl: './truck-journey-list.component.html',
  styleUrls: ['./truck-journey-list.component.scss']
})
export class TruckJourneyListComponent implements OnChanges, OnInit, OnDestroy {

  @ViewChild('dataFilters', {static: true}) private filtersComponent: DataFiltersComponent;

  @Input() public selectedCarrier: Carrier;

  public isLoading: Observable<boolean>;
  public noResult = false;
  public searchValidity: SearchValidity;

  private filtersBase: TruckJourneySearchCriteria = {
    carrierId: undefined,
    loadNumber: '',
    vehicle: '',
    plannedDepartureFrom: moment().startOf('day'),
    plannedDepartureTo: moment().startOf('day').add(LOAD_FETCH_MAX_TIME_WINDOW_IN_DAYS, 'days'),
    pickLocationGlids: [],
    dropLocationGlids: []
  };

  public filters: TruckJourneySearchCriteria = {...this.filtersBase};
  public truckJourneysPerDayGroups: Map<string, TruckJourneyDayGroup>;
  public truckJourneyOrdering: TruckJourneyInfoOpenable[] = [];
  protected readonly noop = noop;
  public readonly HelperPosition = HELPER_POSITION;
  protected readonly GroupStatus = GroupStatus;

  private openSubscription: Subscription;
  private searchValidityMap: Map<SearchValidityKey, SearchValidity> = new Map<SearchValidityKey, SearchValidity>();

  constructor(
    private truckJourneyService: TruckJourneyService,
    private navigation: NavigationService,
    private errorHandler: ErrorHandlerService,
    private translatedToastrService: TranslatedToastrService,
    private loader: LoaderService,
    private cdRef: ChangeDetectorRef
  ) {
    this.isLoading = this.loader.getObservable().pipe(map(loadParam => loadParam.loading));
    this.searchValidityMap.set(SearchValidityKey.IS_VALID, { isValid: true, color: null, messageKey: undefined });
    this.searchValidityMap.set(
      SearchValidityKey.NO_CARRIER_SELECTED,
      { isValid: false, color: null, messageKey: 'truck-journeys.please-select-carrier' }
    );
    this.searchValidity = this.searchValidityMap.get(SearchValidityKey.IS_VALID);
  }

  public ngOnInit(): void {
    if (this.filtersComponent) {
      this.filtersComponent.getResetObservable().subscribe(() => this.resetFilters());
      this.filtersComponent.getApplyObservable().subscribe(() => this.searchTruckJourneys());
    }
  }

  public ngOnChanges(): void {
    this.filtersBase.carrierId = this.selectedCarrier?.id;
    this.resetFilters();
    if (this.selectedCarrier) {
      this.searchTruckJourneys();
    }
    this.checkSearchValidity();
  }

  public checkSearchValidity(fieldNameChanged: string = undefined): void {
    if (!this.selectedCarrier) {
      this.searchValidity = this.searchValidityMap.get(SearchValidityKey.NO_CARRIER_SELECTED);
    } else if (moment(this.filters.plannedDepartureFrom).add(8, 'days').isBefore(this.filters.plannedDepartureTo)) {
      if (fieldNameChanged === 'plannedDepartureTo') {
        this.filters.plannedDepartureFrom = moment(this.filters.plannedDepartureTo).subtract(8, 'days');
      } else if (fieldNameChanged === 'plannedDepartureFrom') {
        this.filters.plannedDepartureTo = moment(this.filters.plannedDepartureFrom).add(8, 'days');
      }
      this.cdRef.detectChanges();
    } else {
      this.searchValidity = this.searchValidityMap.get(SearchValidityKey.IS_VALID);
    }
  }

  public searchTruckJourneys(): void {
    if (!this.selectedCarrier) {
      return;
    }
    this.loader.startLoad();
    if (this.openSubscription) {
      this.openSubscription.unsubscribe();
    }
    this.openSubscription = this.truckJourneyService.searchTruckJourneys(this.filters)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(truckJourneys => {
        this.truckJourneysPerDayGroups = new Map();
        if (truckJourneys?.length === 0) {
          this.noResult = true;
        } else {
          this.noResult = false;
          this.mapResultPerGroupsOfDate(truckJourneys);
        }
      }, error => {
        this.errorHandler.handleHttpError(error, 'truck-journeys.list.search-error');
      });
  }

  private mapResultPerGroupsOfDate(truckJourneys: TruckJourneyInfo[]): void {
    for (const truckJourney of truckJourneys) {
      const truckJourneyStartOfDayDate = truckJourney.plannedPickupDate.startOf('day').format('Do MMMM YYYY');
      if (this.truckJourneysPerDayGroups.has(truckJourneyStartOfDayDate)) {
        const truckJourneyDayGroupedList = this.truckJourneysPerDayGroups.get(truckJourneyStartOfDayDate);
        if (truckJourneyDayGroupedList.has(truckJourney.vehicle)) {
          truckJourneyDayGroupedList.get(truckJourney.vehicle).truckJourneys.push({...truckJourney, opened: false});
        } else {
          truckJourneyDayGroupedList.set(truckJourney.vehicle, {
            truckJourneys: [{...truckJourney, opened: false}],
            status: GroupStatus.CLOSED
          });
        }
      } else {
        const newTruckJourneysPerDayGroup: TruckJourneyDayGroup = new Map([
          [truckJourney.vehicle, {truckJourneys: [{...truckJourney, opened: false}], status: GroupStatus.CLOSED}]
        ]);
        this.truckJourneysPerDayGroups.set(truckJourneyStartOfDayDate, newTruckJourneysPerDayGroup);
      }
    }
  }

  public resetFilters(): void {
    this.filters = cloneDeep(this.filtersBase);
  }

  public openOrCloseTruckJourneyGroup(truckJourneyGroup: TruckJourneyInfoEditableGroup): void {
    switch (truckJourneyGroup.status) {
      case GroupStatus.CLOSED:
        truckJourneyGroup.status = GroupStatus.OPENED;
        break;
      case GroupStatus.OPENED:
        truckJourneyGroup.status = GroupStatus.CLOSED;
        break;
    }
  }

  public createTruckjourney(): void {
    this.navigation.truckJourneys()
      .create(this.selectedCarrier.id)
      .go();
  }

  public startReorderTruckJourneyGroup(truckJourneyGroup: TruckJourneyInfoEditableGroup): void {
    truckJourneyGroup.status = GroupStatus.EDITED;
    this.truckJourneyOrdering = [];
    this.truckJourneyOrdering.push(...truckJourneyGroup.truckJourneys);
  }

  public moveTruckJourneyToNewPosition(currentLoadIndex: number, newLoadIndex: number): void {
    const truckJourneyToMove = this.truckJourneyOrdering.splice(currentLoadIndex, 1)[0];
    if (truckJourneyToMove) {
      this.truckJourneyOrdering.splice(newLoadIndex, 0, truckJourneyToMove);
    } else {
      this.translatedToastrService.showErrorMessage(
        'truck-journeys.detail.movement-error',
        {pos1: (currentLoadIndex + 1), pos2: (newLoadIndex + 1)}
      );
    }
  }

  cancelEdition(truckJourneyGroup: TruckJourneyInfoEditableGroup) {
    truckJourneyGroup.status = GroupStatus.OPENED;
    this.truckJourneyOrdering = [];
  }

  confirmEdition(truckJourneyGroup: TruckJourneyInfoEditableGroup) {
    this.truckJourneyOrdering.forEach((truckJourney, index) => {
      truckJourney.rank = index;
    });
    this.loader.startLoad(true);
    this.truckJourneyService.reorderTruckJourneys(TruckJourneyRequestMapper.truckJourneysReorderFromTruckJourneysInfo(
      truckJourneyGroup.truckJourneys
    ))
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(() => {
        truckJourneyGroup.truckJourneys = this.truckJourneyOrdering;
        this.truckJourneyOrdering = [];
        truckJourneyGroup.status = GroupStatus.OPENED;
        this.translatedToastrService.showSuccessMessage('truck-journeys.list.reorder-success');
      }, error => {
        this.errorHandler.handleHttpError(error, 'truck-journeys.list.reorder-error');
      });
  }

  public editTruckjourney(id: string, assignLoads: boolean): void {
    this.navigation.truckJourneys()
      .detail(id, this.selectedCarrier.id, assignLoads)
      .go();
  }

  public assignLoads(): void {
    this.navigation.truckJourneys()
      .loadAssignment(this.selectedCarrier.id)
      .go();
  }

  public ngOnDestroy(): void {
    this.openSubscription?.unsubscribe();
  }
}
